import React, { useMemo, useState } from 'react';
import type { SlateDocument, SlatePlugin } from '@udecode/slate-plugins';
import {
  BoldPlugin,
  DEFAULTS_BOLD,
  DEFAULTS_HEADING,
  DEFAULTS_IMAGE,
  DEFAULTS_ITALIC,
  DEFAULTS_LINK,
  DEFAULTS_LIST,
  DEFAULTS_PARAGRAPH,
  DEFAULTS_STRIKETHROUGH,
  DEFAULTS_UNDERLINE,
  deserializeHTMLToDocument,
  EditablePlugins,
  ELEMENT_H1,
  ELEMENT_OL,
  ELEMENT_UL,
  HeadingPlugin,
  HeadingToolbar,
  ImagePlugin,
  ItalicPlugin,
  LinkPlugin,
  ListPlugin,
  MARK_BOLD,
  MARK_ITALIC,
  MARK_STRIKETHROUGH,
  MARK_UNDERLINE,
  ParagraphPlugin,
  pipe,
  serializeHTMLFromNodes,
  StrikethroughPlugin,
  ToolbarElement,
  ToolbarImage,
  ToolbarLink,
  ToolbarList,
  UnderlinePlugin,
  withInlineVoid,
  withLink,
  withList,
} from '@udecode/slate-plugins';
import { createEditor, Node, Text } from 'slate';
import { withHistory } from 'slate-history';
import { ReactEditor, Slate, withReact } from 'slate-react';
import icons from '@bbkAdminUtils/icons';
import { BrightbackText } from '@bbkAdminUtils/brightback-expression';
import {
  BbkExpressionPlugin,
  DEFAULTS_BBK_EXPRESSION,
  ELEMENT_BBK_EXPRESSION,
  ToolbarBbkExpression,
} from '@bbkAdminComponents/rich-text-editor/bbk-expression';
import {
  BbkExpressionPluginCleanExport,
  DEFAULTS_BBK_EXPRESSION_CLEAN_EXPORT,
} from '@bbkAdminComponents/rich-text-editor/bbk-expression-export';
import { MarkButton } from './components';
import './styles/toolbar.scss';
import { useBbkExpression } from './bbk-expression/useBbkExpression';
import { BbkMentionSelect } from './bbk-expression/components/MentionSelect';
import {
  allowAlertFieldsForAgs,
  useGetAlertFields,
} from '@bbkAdminRedux/rtkq/integrations.slice';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { cs } from '@bbkAdminUtils/css-utils';
import { cleanDivWrapper } from '@bbkAdminComponents/rich-text-editor/utils';
import { type EnumLike } from '@bbkAdminUtils/utility-types';
import { type AppFieldType } from '@bbkAdminRedux/data-management/reducers';

export const options = {
  // default elements
  ...DEFAULTS_PARAGRAPH,
  ...DEFAULTS_HEADING,
  ...DEFAULTS_LINK,
  ...DEFAULTS_LIST,
  ...DEFAULTS_IMAGE,
  // marks
  ...DEFAULTS_BOLD,
  ...DEFAULTS_ITALIC,
  ...DEFAULTS_UNDERLINE,
  ...DEFAULTS_STRIKETHROUGH,
  // custom elements
  ...DEFAULTS_BBK_EXPRESSION,
  ...DEFAULTS_BBK_EXPRESSION_CLEAN_EXPORT,
  // Configure heading levels
  h1: {
    ...DEFAULTS_HEADING.h1,
  },
};

export type RTESuggestion = {
  val: string;
  key: string;
  type: EnumLike<typeof AppFieldType>;
  field_name?: string;
  integration_id?: number;
  is_lov?: boolean;
  fallback?: string;
  fallBackValue?: string;
  name: string;
  standard?: boolean;
  alert?: boolean;
  reserved?: boolean;
  is_ai?: boolean;
  topMethod?: 'value' | 'default';
};

export type TextEditorButtons = {
  bold?: boolean;
  underline?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  heading?: boolean;
  orderedList?: boolean;
  unorderedList?: boolean;
  fields?: boolean;
  urlFields?: boolean;
  alertFields?: boolean;
  link?: boolean;
  image?: boolean;
};

export type RTEOutputType = 'html' | 'plaintext' | 'structuredPlain';

type Props = {
  value?: string;
  onChange: (value: string) => unknown;
  suggestions?: RTESuggestion[];
  urlSuggestions?: RTESuggestion[];
  enabledButtons?: TextEditorButtons;
  outputType?: RTEOutputType;
  toolbarAtTop?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
};

const replacePercent = (text: string): string =>
  text.replace(/%(?!\d|[ABCDEF]+)/gi, '%25');
const replaceSingleQuotes = (text: string): string =>
  text.replace(/&#x27;/gi, "'");
const changeDivsToPs = (text: string): string =>
  text.replace(/<(\/)?div/g, '<$1p');
const changesPsToDivs = (text: string): string =>
  text.replace(/<(\/)?p/g, '<$1div');
const addClassToEmptyDivs = (text: string): string =>
  text.replace(/<div><\/div>/g, '<div class="slate-div"></div>');

const cleanObjectReferences = (text: string): string =>
  text
    .replace(/attributes="\[object Object\]"/g, '')
    .replace(/element="\[object Object\]"/g, '')
    .replace(/as="[^"]*"/g, '')
    .replace(/styles="\[object Object\]"/g, '');

const convertBbkExpToTextNode = (node: Node): Node => {
  if (Text.isText(node)) {
    return { ...node, text: replacePercent(node.text) };
  }

  const newNode = { ...node };
  if ('type' in newNode && newNode.type === ELEMENT_BBK_EXPRESSION) {
    // console.log(newNode);
    newNode.type = 'bbk-expression-clean-render';
  }

  return {
    ...newNode,
    children: newNode.children.map((n) => convertBbkExpToTextNode(n)),
  };
};

const serializeToBaseText = (nodes?: SlateDocument): string => {
  const buildText = (nodes ?? []).map((n) => Node.string(n)).join('\n');
  return buildText.trimEnd();
};

const serializeToStructuredText = (nodes?: SlateDocument): string => {
  // breaks about child nodes as well
  const buildText = (nodes ?? [])
    .map((n) => n.children.map((c) => Node.string(c)).join('\n'))
    .join('\n');
  return buildText.trimEnd();
};

type PluginFn = (editor: ReactEditor) => ReactEditor;
export const RichTextEditor: React.FC<Props> = (props) => {
  const {
    value: propsValue,
    onChange,
    suggestions,
    urlSuggestions = [],
    enabledButtons: enabledButtonsProps,
    outputType = 'html',
    toolbarAtTop = false,
    placeholder = 'Begin typing…',
    autoFocus = true,
  } = props;

  const alertSuggestions = useGetAlertFields();
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const deflectLicense = productLicense.deflectLicense;

  const filteredAlertSuggestions =
    agsLicense && !deflectLicense
      ? alertSuggestions.filter((alertField) =>
          allowAlertFieldsForAgs.includes(alertField?.key)
        )
      : alertSuggestions;

  const enabledButtons = {
    bold: outputType === 'html',
    italic: outputType === 'html',
    underline: outputType === 'html',
    strikethrough: false,
    heading: false,
    orderedList: false,
    unorderedList: false,
    fields: true,
    urlFields: false,
    alertFields: false,
    link: false,
    image: false,
    ...enabledButtonsProps,
  };
  const enabledFields =
    enabledButtons.fields ||
    enabledButtons.urlFields ||
    enabledButtons.alertFields;

  const plugins: SlatePlugin[] = [ParagraphPlugin(options)];
  if (enabledButtons.heading) {
    plugins.push(HeadingPlugin({ ...options, levels: 6 }));
  }
  if (enabledButtons.bold) plugins.push(BoldPlugin(options));
  if (enabledButtons.italic) plugins.push(ItalicPlugin(options));
  if (enabledButtons.underline) plugins.push(UnderlinePlugin(options));
  if (enabledButtons.strikethrough) plugins.push(StrikethroughPlugin(options));
  if (enabledButtons.orderedList || enabledButtons.unorderedList) {
    plugins.push(ListPlugin(options));
  }
  if (enabledButtons.link) plugins.push(LinkPlugin(options));
  if (enabledButtons.image) plugins.push(ImagePlugin(options));
  if (enabledFields) {
    plugins.push(BbkExpressionPlugin(options));
    plugins.push(BbkExpressionPluginCleanExport(options));
  }

  const withPlugins: PluginFn[] = [
    withReact,
    withHistory,
    withInlineVoid({ plugins }),
    ...(enabledButtons.link ? [withLink(options)] : []),
    ...(enabledButtons.orderedList || enabledButtons.unorderedList
      ? [withList(options)]
      : []),
  ];

  const wrappedHtml = new BrightbackText(
    propsValue!
  ).wrapExpressionsInMentions();
  const preConvertHTML = changeDivsToPs(wrappedHtml);
  const { body } = new DOMParser().parseFromString(preConvertHTML, 'text/html');
  const parsedSlateDocument = deserializeHTMLToDocument({
    plugins,
    element: body,
  });

  const [value, setValue] = useState<SlateDocument>(parsedSlateDocument);
  const editor = useMemo(() => {
    const baseEditor = createEditor() as ReactEditor;
    return pipe(baseEditor, ...withPlugins);
  }, []);

  const bbkExpression = useBbkExpression(suggestions, {
    maxSuggestions: 10,
    trigger: '@',
  });

  const urlBbkExpression = useBbkExpression(urlSuggestions, {
    maxSuggestions: 10,
    trigger: '@',
  });

  const alertBbkExpression = useBbkExpression(filteredAlertSuggestions, {
    maxSuggestions: 10,
    trigger: '@',
  });

  const showToolbar = Object.values(enabledButtons).filter((v) => v).length > 0;

  const ele = (
    <>
      {showToolbar && (
        <HeadingToolbar>
          {enabledButtons.heading && (
            <ToolbarElement type={ELEMENT_H1} icon={icons.heading} />
          )}
          {enabledButtons.bold && (
            <MarkButton format={MARK_BOLD} icon={icons.bold} />
          )}
          {enabledButtons.italic && (
            <MarkButton format={MARK_ITALIC} icon={icons.italics} />
          )}
          {enabledButtons.underline && (
            <MarkButton format={MARK_UNDERLINE} icon={icons.underline} />
          )}
          {enabledButtons.strikethrough && (
            <MarkButton
              format={MARK_STRIKETHROUGH}
              icon={icons.strikethrough}
            />
          )}
          {enabledButtons.unorderedList && (
            <ToolbarList
              className="unordered-list-toolbar-button"
              typeList={ELEMENT_UL}
              icon={icons.unorderedList}
              {...options}
            />
          )}
          {enabledButtons.orderedList && (
            <ToolbarList
              className="ordered-list-toolbar-button"
              typeList={ELEMENT_OL}
              icon={icons.orderedList}
              {...options}
            />
          )}
          {enabledButtons.image && (
            <ToolbarImage icon={icons.image} {...options} />
          )}
          {enabledButtons.link && (
            <ToolbarLink icon={icons.link} {...options} />
          )}
          {enabledButtons.urlFields && (
            <ToolbarBbkExpression
              {...options}
              icon={icons.globe}
              onToolbarMentionClick={urlBbkExpression.onToolbarMentionClick}
              activateMentionViaToolbar={
                urlBbkExpression.activateMentionViaToolbar
              }
            />
          )}
          {enabledButtons.fields && (
            <ToolbarBbkExpression
              {...options}
              icon={icons.lightning}
              onToolbarMentionClick={bbkExpression.onToolbarMentionClick}
              activateMentionViaToolbar={
                bbkExpression.activateMentionViaToolbar
              }
            />
          )}
          {enabledButtons.alertFields && (
            <ToolbarBbkExpression
              {...options}
              icon={icons.bell}
              onToolbarMentionClick={alertBbkExpression.onToolbarMentionClick}
              activateMentionViaToolbar={
                alertBbkExpression.activateMentionViaToolbar
              }
            />
          )}
        </HeadingToolbar>
      )}

      <EditablePlugins
        plugins={plugins}
        placeholder={placeholder}
        onKeyDown={[
          bbkExpression.onKeyDownBbkExpression,
          ...(enabledButtons.urlFields
            ? [urlBbkExpression.onKeyDownBbkExpression]
            : []),
          ...(enabledButtons.alertFields
            ? [alertBbkExpression.onKeyDownBbkExpression]
            : []),
        ]}
        onKeyDownDeps={[
          bbkExpression.index,
          bbkExpression.search,
          bbkExpression.target,
          ...(enabledButtons.urlFields
            ? [
                urlBbkExpression.index,
                urlBbkExpression.search,
                urlBbkExpression.target,
              ]
            : []),
          ...(enabledButtons.alertFields
            ? [
                alertBbkExpression.index,
                alertBbkExpression.search,
                alertBbkExpression.target,
              ]
            : []),
        ]}
        spellCheck
        autoFocus={autoFocus}
        className={cs(
          'tw-min-h-10 tw-space-y-2 tw-px-2.5 tw-py-1',
          'tw-overflow-hidden !tw-text-sm',
          'tw-rounded-b tw-border tw-border-cbrneutral-300 tw-bg-white tw-text-cbrneutral-700',
          {
            'tw-rounded': !showToolbar,
            'tw-border-t-0': showToolbar,
          }
        )}
      />

      <BbkMentionSelect
        at={bbkExpression.target}
        valueIndex={bbkExpression.index}
        fields={bbkExpression.values}
        allFields={suggestions!}
        onAddMention={bbkExpression.onAddBbkExpression}
        fallback={bbkExpression.fallback}
        fallbackRef={bbkExpression.fallbackRef}
        onChangeFallback={bbkExpression.onChangeFallback}
        activateMentionViaToolbar={bbkExpression.activateMentionViaToolbar!}
        onMouseClickToolbarMention={bbkExpression.onMouseClickToolbarMention}
        closeMentionSelect={bbkExpression.closeMentionSelect}
      />

      {enabledButtons.urlFields && (
        <BbkMentionSelect
          at={urlBbkExpression.target}
          valueIndex={urlBbkExpression.index}
          fields={urlBbkExpression.values}
          allFields={urlSuggestions}
          onAddMention={urlBbkExpression.onAddBbkExpression}
          fallback={urlBbkExpression.fallback}
          fallbackRef={urlBbkExpression.fallbackRef}
          onChangeFallback={urlBbkExpression.onChangeFallback}
          activateMentionViaToolbar={
            urlBbkExpression.activateMentionViaToolbar!
          }
          onMouseClickToolbarMention={
            urlBbkExpression.onMouseClickToolbarMention
          }
          closeMentionSelect={urlBbkExpression.closeMentionSelect}
        />
      )}
      {enabledButtons.alertFields && (
        <BbkMentionSelect
          at={alertBbkExpression.target}
          valueIndex={alertBbkExpression.index}
          fields={alertBbkExpression.values}
          allFields={filteredAlertSuggestions}
          onAddMention={alertBbkExpression.onAddBbkExpression}
          fallback={alertBbkExpression.fallback}
          fallbackRef={alertBbkExpression.fallbackRef}
          onChangeFallback={alertBbkExpression.onChangeFallback}
          activateMentionViaToolbar={
            alertBbkExpression.activateMentionViaToolbar!
          }
          onMouseClickToolbarMention={
            alertBbkExpression.onMouseClickToolbarMention
          }
          closeMentionSelect={alertBbkExpression.closeMentionSelect}
        />
      )}
    </>
  );

  return (
    <Slate
      editor={editor}
      value={value}
      onChange={(newVal) => {
        const newValue = newVal as SlateDocument;
        setValue(newValue);
        bbkExpression.onChangeBbkExpression(editor);
        if (enabledButtons.urlFields) {
          urlBbkExpression.onChangeBbkExpression(editor);
        }
        if (enabledButtons.alertFields) {
          alertBbkExpression.onChangeBbkExpression(editor);
        }

        let serializedValue;
        switch (outputType) {
          case 'plaintext': {
            serializedValue = serializeToBaseText(newValue);
            break;
          }
          case 'structuredPlain': {
            serializedValue = serializeToStructuredText(newValue);
            break;
          }
          case 'html':
          default: {
            // Converts expressions to new type for rendering
            const preserializeTransform = newValue.map((v) =>
              convertBbkExpToTextNode(v)
            );
            // Serialize to html with custom list handling
            serializedValue = serializeHTMLFromNodes({
              plugins,
              nodes: preserializeTransform,
            });

            // remove object references from serialization
            serializedValue = cleanObjectReferences(serializedValue);
            // need to do this before its saved to the server to avoid problems with replacement
            serializedValue = replaceSingleQuotes(serializedValue);
            serializedValue = cleanDivWrapper(serializedValue);
            serializedValue = changesPsToDivs(serializedValue);
            serializedValue = addClassToEmptyDivs(serializedValue);
          }
        }
        onChange(serializedValue);
      }}
    >
      {toolbarAtTop ? <div className="tw-w-full">{ele}</div> : ele}
    </Slate>
  );
};
